<script setup>
import { computed } from 'vue'
import { useSettingsStore } from '../stores/settingsStore'
import { getOptimizedImageUrl } from '../utils/image'
import NewsletterSection from './NewsletterSection.vue'

const settingsStore = useSettingsStore()

const contactPhones = computed(() => settingsStore.contactPhones || [])
const contactEmails = computed(() => settingsStore.contactEmails || [])
const contactLines = computed(() => settingsStore.contactLines || [])
const contactFacebookUrl = computed(() => settingsStore.contactFacebookUrl)
const contactTiktokUrl = computed(() => settingsStore.contactTiktokUrl)
const contactYoutubeUrl = computed(() => settingsStore.contactYoutubeUrl)
const contactMapUrl = computed(() => settingsStore.resolvedContactMapUrl)

// Primary contact details with admin fallback hierarchy
const primaryContactPhone = computed(() => {
  if (settingsStore.storePhone) return settingsStore.storePhone
  if (contactPhones.value.length > 0) {
    const first = contactPhones.value[0]
    return typeof first === 'object' && first !== null ? (first.value || first.phone || '') : String(first)
  }
  return settingsStore.contactPhone || '02-9081348-9'
})

const primaryContactEmail = computed(() => {
  if (settingsStore.storeEmail) return settingsStore.storeEmail
  if (contactEmails.value.length > 0) {
    const first = contactEmails.value[0]
    return typeof first === 'object' && first !== null ? (first.value || first.email || '') : String(first)
  }
  return settingsStore.contactEmail || 'panuwat@outdoorlander.co.th'
})

const primaryLine = computed(() => {
  if (contactLines.value.length > 0) {
    return contactLines.value[0]
  }
  return { name: 'Outdoor Lander Official', value: '@outdoorlander_official', url: 'https://line.me/ti/p/~@outdoorlander_official' }
})

const scrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>

<template>
  <!-- Modern Daylight Architectural Pre-Footer & Footer (Light by Default + Dark Mode Support) -->
  <footer class="relative z-10 bg-gradient-to-b from-[#f8fafc] via-white to-[#f1f5f9] dark:from-[#050807] dark:via-[#070b09] dark:to-[#050807] text-slate-600 dark:text-gray-300 border-t border-slate-200/80 dark:border-emerald-950/60 overflow-hidden w-full selection:bg-emerald-500 selection:text-white transition-colors duration-300">
    <!-- Top Luminous Accent Line -->
    <div class="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent"></div>

    <!-- Ambient Daylight & Dark Glow Effects -->
    <div class="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none -z-10 overflow-hidden">
      <div class="absolute top-[-8%] left-[-10%] w-[560px] h-[560px] bg-emerald-500/[0.06] dark:bg-emerald-500/[0.05] blur-[140px] rounded-full"></div>
      <div class="absolute bottom-[-12%] right-[-10%] w-[520px] h-[520px] bg-teal-500/[0.06] dark:bg-teal-500/[0.05] blur-[140px] rounded-full"></div>
    </div>

    <div class="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-24 pb-16 sm:pb-20 z-10">

      <!-- ========================================================================= -->
      <!-- SECTION 2: VIP PRIVILEGE & NEWSLETTER LOUNGE (ดีไซน์โทนสว่างพรีเมียมใหม่ทั้งหมด) -->
      <!-- ========================================================================= -->
      <div class="relative rounded-[2.25rem] bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/75 dark:from-[#0b1c12] dark:via-[#08150e] dark:to-[#040806] border border-emerald-200/80 dark:border-emerald-500/25 p-6 sm:p-10 lg:p-12 shadow-[0_20px_60px_-15px_rgba(16,185,129,0.12)] dark:shadow-2xl overflow-hidden">
        <!-- Decorative Soft Daylight Orbs -->
        <div class="absolute -right-24 -top-24 w-96 h-96 bg-emerald-400/15 dark:bg-emerald-500/10 blur-[100px] pointer-events-none rounded-full"></div>
        <div class="absolute -left-24 -bottom-24 w-96 h-96 bg-teal-400/15 dark:bg-teal-500/10 blur-[100px] pointer-events-none rounded-full"></div>

        <div class="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <!-- Left: Privilege Club Story & 3 Visual Perk Bento Chips (7 cols) -->
          <div class="lg:col-span-7">
            <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-extrabold bg-[#0b533b] text-white shadow-sm shadow-emerald-950/20 mb-5">
              <span class="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span>
              OUTDOOR LANDER PRIVILEGE CLUB
            </div>

            <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 dark:text-white leading-[1.2] tracking-tight mb-3.5">
              พื้นที่กลางแจ้ง ให้ชีวิตคุณกว้างขึ้น <br class="hidden sm:block"/>
              <span class="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-500 dark:from-emerald-400 dark:via-teal-300 dark:to-emerald-200">
                เริ่มต้นสร้างพื้นที่ในฝันกับเรา
              </span>
            </h3>

            <p class="text-sm sm:text-base text-slate-600 dark:text-gray-300 leading-relaxed max-w-xl mb-6">
              รับสิทธิพิเศษ ส่วนลดประจำเดือน แคตตาล็อกไอเดียตกแต่งบ้านและสวนกลางแจ้ง พร้อมข้อเสนอสุดพิเศษส่งตรงถึงคุณก่อนใคร
            </p>

            <!-- 3 Elevated Perk Chips -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div class="flex items-center gap-3 p-3 rounded-2xl bg-white/90 dark:bg-white/[0.05] border border-emerald-100 dark:border-white/10 shadow-sm">
                <div class="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 flex items-center justify-center shrink-0">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>
                </div>
                <span class="text-xs font-bold text-slate-800 dark:text-gray-200 leading-snug">รับสิทธิ์โปรโมชั่น Payday</span>
              </div>

              <div class="flex items-center gap-3 p-3 rounded-2xl bg-white/90 dark:bg-white/[0.05] border border-emerald-100 dark:border-white/10 shadow-sm">
                <div class="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 flex items-center justify-center shrink-0">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>
                </div>
                <span class="text-xs font-bold text-slate-800 dark:text-gray-200 leading-snug">ดาวน์โหลดแคตตาล็อก 2026 ฟรี</span>
              </div>

              <div class="flex items-center gap-3 p-3 rounded-2xl bg-white/90 dark:bg-white/[0.05] border border-emerald-100 dark:border-white/10 shadow-sm">
                <div class="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 flex items-center justify-center shrink-0">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>
                </div>
                <span class="text-xs font-bold text-slate-800 dark:text-gray-200 leading-snug">คำนวณพื้นที่หน้างานฟรี</span>
              </div>
            </div>
          </div>

          <!-- Right: Floating VIP Subscription Card (5 cols) -->
          <div class="lg:col-span-5 w-full">
            <div class="bg-white dark:bg-[#0d1712] rounded-3xl p-6 sm:p-7 border border-emerald-200/80 dark:border-emerald-500/25 shadow-xl shadow-emerald-950/5">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-800 to-teal-800 text-white flex items-center justify-center shadow-md shadow-emerald-950/20 shrink-0">
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-sm sm:text-base font-black text-slate-900 dark:text-white">ลงทะเบียนสมาชิก VIP ฟรี</h4>
                  <p class="text-xs text-slate-600 dark:text-slate-300">รับข่าวสารและโค้ดส่วนลดพิเศษก่อนใคร</p>
                </div>
              </div>

              <NewsletterSection />

              <div class="mt-4 pt-3.5 border-t border-slate-100 dark:border-white/10 flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-300">
                <span class="inline-flex items-center gap-1.5">
                  <svg class="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  ข้อมูลปลอดภัย 100% ไม่ส่งสแปม
                </span>
                <router-link to="/quotation" class="font-bold text-emerald-800 dark:text-emerald-300 hover:underline">
                  ขอใบเสนอราคาด่วน &rarr;
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- BLACK LUXURY FOOTER DIRECTORY & LEGAL BAR (SECTION 3 & 4 - พื้นหลังสีดำ) -->
    <!-- ========================================================================= -->
    <div class="relative bg-[#050807] text-gray-300 border-t border-emerald-950/70 overflow-hidden">
      <!-- Top Luminous Emerald Divider Line -->
      <div class="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent"></div>

      <!-- Ambient Glow Effects on Black Background -->
      <div class="absolute inset-0 max-w-7xl mx-auto pointer-events-none overflow-hidden">
        <div class="absolute top-[-15%] left-[-10%] w-[520px] h-[520px] bg-emerald-500/[0.06] blur-[140px] rounded-full"></div>
        <div class="absolute bottom-[-20%] right-[-10%] w-[480px] h-[480px] bg-teal-500/[0.05] blur-[140px] rounded-full"></div>
      </div>

      <!-- Huge Elegant Brand Watermark -->
      <div class="absolute bottom-24 left-0 right-0 flex justify-center pointer-events-none select-none overflow-hidden opacity-[0.03]">
        <span class="text-[13vw] font-black tracking-[0.22em] text-white leading-none whitespace-nowrap">OUTDOOR LANDER</span>
      </div>

      <div class="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 pb-12 z-10">
        <!-- SECTION 3: MAIN NAVIGATION & CONTACT DIRECTORY (5 COLUMNS) -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12 mb-16">
          
          <!-- Column 1: Brand & Headquarter Details (4 cols) -->
          <div class="lg:col-span-4 flex flex-col items-start lg:pr-6">
            <router-link to="/" class="group inline-flex items-center gap-3.5 mb-5">
              <template v-if="settingsStore.storeLogo">
                <img 
                  :src="getOptimizedImageUrl(settingsStore.storeLogo, 300)" 
                  :srcset="`${getOptimizedImageUrl(settingsStore.storeLogo, 200)} 200w, ${getOptimizedImageUrl(settingsStore.storeLogo, 300)} 300w, ${getOptimizedImageUrl(settingsStore.storeLogo, 400)} 400w`"
                  sizes="(max-width: 640px) 160px, 220px"
                  alt="Outdoor Lander Logo" 
                  width="220" 
                  height="65" 
                  class="h-10 sm:h-11 w-auto object-contain drop-shadow-[0_4px_12px_rgba(16,185,129,0.25)] transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </template>
              <template v-else>
                <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center font-black shadow-lg shadow-emerald-950/60 border border-emerald-400/40">
                  OL
                </div>
                <span class="text-2xl font-black tracking-tight text-white group-hover:text-emerald-300 transition-colors">
                  {{ settingsStore.storeName || 'Outdoor Lander' }}
                </span>
              </template>
            </router-link>

            <p class="text-sm leading-relaxed text-gray-400 mb-6 max-w-sm">
              {{ settingsStore.storeDescription || 'ผู้นำด้านบ้านเก็บของสำเร็จรูป โรงเรือนเพาะปลูก และอุปกรณ์จัดสรรพื้นที่กลางแจ้งระดับพรีเมียม ครบวงจรทั้งการออกแบบและติดตั้ง' }}
            </p>

            <!-- Working Hours Pill -->
            <div class="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-emerald-500/[0.1] border border-emerald-500/25 text-xs font-bold text-emerald-300 mb-6">
              <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0"></span>
              <span>เวลาทำการ: จันทร์ - ศุกร์ 08:00 - 17:00 น.</span>
            </div>

            <!-- Address & Tax ID Details -->
            <div class="space-y-3.5 text-xs text-gray-400">
              <div v-if="settingsStore.storeAddress || settingsStore.contactAddress" class="flex items-start gap-3">
                <svg class="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <span class="leading-relaxed">{{ settingsStore.storeAddress || settingsStore.contactAddress }}</span>
              </div>

              <div v-if="settingsStore.storeTaxId || settingsStore.contactTaxId" class="flex items-center gap-3">
                <svg class="w-4 h-4 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span>เลขประจำตัวผู้เสียภาษี: {{ settingsStore.storeTaxId || settingsStore.contactTaxId }}</span>
              </div>
            </div>
          </div>

          <!-- Column 2: Product Categories (2 cols) -->
          <div class="lg:col-span-2">
            <div class="flex items-center gap-2 mb-6">
              <span class="w-1.5 h-4 rounded-full bg-emerald-500"></span>
              <h4 class="text-sm font-black text-white uppercase tracking-wider">สินค้าและหมวดหมู่</h4>
            </div>
            <ul class="flex flex-col gap-3 text-sm">
              <li>
                <router-link to="/products" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  บ้านเก็บของสำเร็จรูป
                </router-link>
              </li>
              <li>
                <router-link to="/products" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  โรงเรือนเพาะปลูก
                </router-link>
              </li>
              <li>
                <router-link to="/products" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  กล่องเก็บของกลางแจ้ง
                </router-link>
              </li>
              <li>
                <router-link to="/promotions" class="text-amber-400 hover:text-amber-300 font-bold transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-amber-400 group-hover:w-2.5 transition-all"></span>
                  โปรโมชั่นพิเศษ
                </router-link>
              </li>
              <li>
                <router-link to="/products" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  สินค้าทั้งหมด
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Column 3: Services & Tools (2 cols) -->
          <div class="lg:col-span-2">
            <div class="flex items-center gap-2 mb-6">
              <span class="w-1.5 h-4 rounded-full bg-emerald-500"></span>
              <h4 class="text-sm font-black text-white uppercase tracking-wider">บริการและระบบ</h4>
            </div>
            <ul class="flex flex-col gap-3 text-sm">
              <li>
                <router-link to="/space-calculator" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  คำนวณพื้นที่จัดวาง
                </router-link>
              </li>
              <li>
                <router-link to="/quotation" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  ขอใบเสนอราคาออนไลน์
                </router-link>
              </li>
              <li>
                <router-link to="/services" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  บริการจัดส่งและติดตั้ง
                </router-link>
              </li>
              <li>
                <router-link to="/projects" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  ผลงานติดตั้งจริง
                </router-link>
              </li>
              <li>
                <router-link to="/track-order" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  ตรวจสอบสถานะพัสดุ
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Column 4: Help & Policy (2 cols) -->
          <div class="lg:col-span-2">
            <div class="flex items-center gap-2 mb-6">
              <span class="w-1.5 h-4 rounded-full bg-emerald-500"></span>
              <h4 class="text-sm font-black text-white uppercase tracking-wider">ศูนย์ช่วยเหลือ</h4>
            </div>
            <ul class="flex flex-col gap-3 text-sm">
              <li>
                <router-link to="/about" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  เกี่ยวกับเรา
                </router-link>
              </li>
              <li>
                <router-link to="/blog" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  บทความและสาระน่ารู้
                </router-link>
              </li>
              <li>
                <router-link to="/contact" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  ติดต่อเราและแผนที่
                </router-link>
              </li>
              <li>
                <router-link to="/warranty-policy" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  นโยบายการรับประกัน
                </router-link>
              </li>
              <li>
                <router-link to="/privacy-policy" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  นโยบายความเป็นส่วนตัว
                </router-link>
              </li>
              <li>
                <router-link to="/terms-of-service" class="text-gray-400 hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/60 group-hover:w-2.5 group-hover:bg-emerald-400 transition-all"></span>
                  เงื่อนไขการใช้บริการ
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Column 5: Direct Contact Hub (2 cols) -->
          <div class="lg:col-span-2">
            <div class="flex items-center gap-2 mb-6">
              <span class="w-1.5 h-4 rounded-full bg-emerald-500"></span>
              <h4 class="text-sm font-black text-white uppercase tracking-wider">ติดต่อเรา</h4>
            </div>

            <div class="flex flex-col gap-3">
              <!-- Dynamic Phone Link Card -->
              <a 
                :href="'tel:' + primaryContactPhone" 
                class="group flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] hover:bg-emerald-500/[0.12] border border-white/10 hover:border-emerald-500/40 transition-all duration-300"
                title="โทรติดต่อทีมงาน"
              >
                <div class="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                </div>
                <div class="min-w-0">
                  <div class="text-[11px] text-gray-400">โทรปรึกษาทีมงาน</div>
                  <div class="text-xs font-bold text-white group-hover:text-emerald-300 transition-colors truncate">{{ primaryContactPhone }}</div>
                </div>
              </a>

              <!-- Dynamic Email Link Card -->
              <a 
                :href="'mailto:' + primaryContactEmail" 
                class="group flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] hover:bg-emerald-500/[0.12] border border-white/10 hover:border-emerald-500/40 transition-all duration-300"
                title="ส่งอีเมลติดต่อ"
              >
                <div class="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div class="min-w-0">
                  <div class="text-[11px] text-gray-400">อีเมลติดต่อ</div>
                  <div class="text-xs font-bold text-white group-hover:text-emerald-300 transition-colors truncate">{{ primaryContactEmail }}</div>
                </div>
              </a>

              <!-- LINE Official Card -->
              <a 
                :href="primaryLine.url || 'https://line.me/ti/p/~' + primaryLine.value.replace(/^@/, '')" 
                target="_blank" 
                rel="noopener noreferrer"
                class="group flex items-center gap-3 p-3 rounded-xl bg-[#06C755]/10 hover:bg-[#06C755]/20 border border-[#06C755]/25 hover:border-[#06C755]/50 transition-all duration-300"
              >
                <div class="w-8 h-8 rounded-lg bg-[#06C755] text-white flex items-center justify-center shrink-0 shadow-sm shadow-[#06C755]/30 group-hover:scale-105 transition-transform">
                  <svg viewBox="0 0 24 24" class="w-4 h-4" fill="currentColor">
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61V9.86h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.205 0 .391.09.51.253l2.444 3.317V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                  </svg>
                </div>
                <div class="min-w-0">
                  <div class="text-[11px] text-[#06C755] font-bold">LINE Official</div>
                  <div class="text-xs font-bold text-white group-hover:text-[#06C755] transition-colors truncate">@{{ primaryLine.value.replace(/^@/, '') }}</div>
                </div>
              </a>

              <!-- Google Maps Button -->
              <a 
                v-if="contactMapUrl" 
                :href="contactMapUrl" 
                target="_blank" 
                rel="noopener noreferrer"
                class="group flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] hover:bg-rose-500/[0.12] border border-white/10 hover:border-rose-500/40 transition-all duration-300"
              >
                <div class="w-8 h-8 rounded-lg bg-rose-500/20 text-rose-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                </div>
                <div class="min-w-0">
                  <div class="text-[11px] text-gray-400">โชว์รูมและคลังสินค้า</div>
                  <div class="text-xs font-bold text-white group-hover:text-rose-300 transition-colors">เปิดดู Google Maps</div>
                </div>
              </a>
            </div>
          </div>

        </div>

        <!-- SECTION 4: BOTTOM LEGAL, PAYMENT & SOCIAL BAR -->
        <div class="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6">
          
          <!-- Copyright & Certification -->
          <div class="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-xs text-gray-400 text-center sm:text-left">
            <p>
              &copy; {{ new Date().getFullYear() }} <span class="text-white font-bold">{{ settingsStore.storeName || 'Outdoor Lander' }}</span>. สงวนลิขสิทธิ์ทั้งหมด
            </p>
            <span class="hidden sm:inline text-white/20">|</span>
            <span class="text-gray-400">พื้นที่กลางแจ้ง ให้ชีวิตคุณกว้างขึ้น</span>
          </div>

          <!-- Payment Badges (PromptPay, Visa, Mastercard) -->
          <div class="flex items-center gap-2.5">
            <div class="px-2.5 py-1 rounded-md bg-white/[0.06] border border-white/10 text-[10px] font-bold text-gray-300 tracking-wider">
              PROMPTPAY
            </div>
            <div class="flex items-center justify-center h-7 px-2.5 bg-white/[0.06] border border-white/10 rounded-md">
              <svg viewBox="0 0 38 12" class="h-2.5 text-[#3b82f6]" fill="currentColor">
                <path d="M14.743 11.666L16.275 1.836h2.463l-1.532 9.83h-2.463zm8.397-9.585c-.911-.428-2.342-.71-3.955-.71-2.43 0-4.144 1.258-4.157 3.061-.02 1.332 1.229 2.078 2.164 2.525.962.459 1.282.753 1.282 1.163-.013.626-.78.917-1.498.917-1.255 0-1.924-.184-2.951-.628l-.42-.191-.346 2.072c.732.327 2.078.614 3.486.626 2.585 0 4.275-1.236 4.296-3.149.02-1.077-.638-1.89-2.062-2.551-.869-.434-1.401-.723-1.401-1.163.013-.414.475-.845 1.442-.845.811-.013 1.419.167 1.888.351l.228.103.339-1.996zM28.468 11.666h2.179l-1.921-9.83h-2.025c-.47 0-.853.261-1.036.685l-3.565 8.271.01-.044h2.573s.42-.113.514-.143h3.141c.079.034.298.143.298.143l-1.168.861zm-2.415-2.732l1.01-2.613c-.02.04.208-.544.331-.892.052-.14.099-.281.144-.419.014.041.026.084.037.126l.462 2.14c.032.148.067.3.1.455h-2.084zm-14.887 2.732h-2.48l-1.579-6.666c-.198-.813-.811-1.406-1.547-1.637l-2.124-.467v-.218h3.585c.613 0 1.169.406 1.321 1.054l1.229 5.867 1.942-7.933h2.56l-2.907 9.83z"/>
              </svg>
            </div>
            <div class="flex items-center justify-center h-7 px-2.5 bg-white/[0.06] border border-white/10 rounded-md">
              <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor">
                <circle cx="9" cy="12" r="5.5" fill="#eb001b"/>
                <circle cx="15" cy="12" r="5.5" fill="#f79e1b"/>
                <path d="M12 16.5a5.485 5.485 0 010-9 5.485 5.485 0 010 9z" fill="#ff5f00"/>
              </svg>
            </div>
          </div>

          <!-- Social Channels & Back To Top -->
          <div class="flex items-center gap-3">
            <!-- Facebook -->
            <a 
              v-if="contactFacebookUrl" 
              :href="contactFacebookUrl" 
              aria-label="Facebook" 
              target="_blank" 
              rel="noopener noreferrer" 
              class="w-9 h-9 rounded-full bg-white/[0.06] hover:bg-[#1877F2] border border-white/10 hover:border-[#1877F2] text-gray-300 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-sm"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>

            <!-- TikTok -->
            <a 
              v-if="contactTiktokUrl" 
              :href="contactTiktokUrl" 
              aria-label="TikTok" 
              target="_blank" 
              rel="noopener noreferrer" 
              class="w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white border border-white/10 hover:border-white text-gray-300 hover:text-black flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-sm"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.76-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.16-3.44-3.37-3.46-5.7-.02-1.29.3-2.58.95-3.71A5.98 5.98 0 015.65 10.7a6.002 6.002 0 015.54-2.12v4.11c-.57-.02-1.14.07-1.68.25-.92.32-1.74 1.02-2.13 1.91-.32.74-.35 1.56-.16 2.33.15.63.47 1.25.93 1.7.9.89 2.3 1.21 3.52.8.93-.31 1.74-1.09 2.05-2.02.16-.48.26-1.01.24-1.52.01-4.71.01-9.42.01-14.13z"/>
              </svg>
            </a>

            <!-- YouTube -->
            <a 
              v-if="contactYoutubeUrl" 
              :href="contactYoutubeUrl" 
              aria-label="YouTube" 
              target="_blank" 
              rel="noopener noreferrer" 
              class="w-9 h-9 rounded-full bg-white/[0.06] hover:bg-[#FF0000] border border-white/10 hover:border-[#FF0000] text-gray-300 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-sm"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>

            <!-- Back To Top Button -->
            <button 
              @click="scrollToTop" 
              aria-label="เลื่อนขึ้นด้านบน"
              class="ml-2 w-9 h-9 rounded-full bg-emerald-500/15 hover:bg-emerald-500 border border-emerald-500/30 hover:border-emerald-500 text-emerald-400 hover:text-white flex items-center justify-center transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              title="กลับสู่ด้านบน"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 15l7-7 7 7"/>
              </svg>
            </button>
          </div>

        </div>
      </div>
    </div>
  </footer>
</template>
